import { FC, useEffect, useState } from "react";
import { v4 as uuidv4} from 'uuid';
import { LoadingStatus } from "../../../@types/enumsGlobal";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import { Spinner } from "../../../components/spiner/Spinner";
import useGridFilter, { CreateSelectorGridFilter } from "../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { useUserContext } from "../../../system/providers/userContextProvider";
import { checkAccessStatus, checkLockStatus } from "../../CommonHelperFunctions";
import { ILoyaltyProgramDTO, ILoyaltyProgramEditDTO } from "../../../libs/coreapi-dto/dirs/loyaltyProgram";
import { LoyaltyProgramDataProvider } from "../../../Services/DataProviders/LoyaltyProgramDataProvider";
import LoyaltyProgramCreatorModal from "./LoyaltyProgramCreatorModal";
import { LoyaltyProgramPanel } from "./components/LoyaltyProgramPanel";
import { User2ContractorDataProvider } from "../../../Services/DataProviders/User2ContractorDataProvider";
import { IUser2ContractorViewDTO } from "../../../libs/coreapi-dto/dirs/user2Contractor";

export const LoyaltyProgramGrid: FC<IGridProps> = (props) => {
    const appCtx = useAppContext()
    const userContext = useUserContext()

    const LPDataProvider = new LoyaltyProgramDataProvider(appCtx.coreApiService);
    const user2ContractorDataProvider = new User2ContractorDataProvider(appCtx.coreApiService);

    const isAdministrator = userContext.userRoles.includes('Administrator');
    const [data, setData] = useState<ILoyaltyProgramDTO[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>();
    const [totalCount, setTotalCount] = useState(0);
    const [viewState, setViewState] = useState<GridStateType>("view");
    const [gridFilter, dispatchGridFilter] = useGridFilter(props.baseGridFilter ?? CreateSelectorGridFilter);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>(props.data ?? []);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const [modalWindow, setModalWindow] = useState(<></>)
    const lockFromPermission = checkLockStatus(props.plugin.permission as IPermission, userContext.userPermission);
    const [contractors, setContractors] = useState<IUser2ContractorViewDTO[]>([]);
    const [isContractorsLoading, setIsContractorsLoading] = useState(!isAdministrator);
    
    const defaultFilter = {
        pageNumber: 1,
        numberPerPage: 256,
        columnFilters: [
            {
                name: "idUserGlobal",
                value: userContext.idGlobal,
                operator: "Eq",
                invisible: true
            } as IGridColumnFilter
        ]
    } as IGridFilter;

    useEffect(() => {
        if (!isAdministrator) {
            setIsContractorsLoading(true);
            user2ContractorDataProvider.getView(defaultFilter, (values) => {
                setContractors(values);
                setIsContractorsLoading(false);
            })
        } else {
            setIsContractorsLoading(false);
        }
    }, [isAdministrator])
    
    useEffect(() => {
        setIsSubmitting(true)
        LPDataProvider.getView(gridFilter, async (data) => {
            let filteredData: ILoyaltyProgramDTO[] = [];

            if(isAdministrator) {
                filteredData = data;
            } else {
                filteredData = data.filter((program) => contractors.some(contractor => contractor.idContractorGlobal === program.idContractorGlobal));
            }
            setData(filteredData)
            setTotalCount(filteredData.length)
            setLoadingStatus(LoadingStatus.Completed)
            setIsSubmitting(false)
        })
    }, [gridFilter, contractors, isAdministrator])

    useEffect(() => {
        if (!isAdministrator && isContractorsLoading) return;

        switch (viewState) {
            case 'refresh':
                setIsSubmitting(true)
                LPDataProvider.getView(gridFilter, async (data, totalCount) => {
                    let filteredData: ILoyaltyProgramDTO[] = [];
                    if (isAdministrator) {
                        filteredData = data;
                    } else {
                        filteredData = data.filter((program) => 
                            contractors.some(contractor => contractor.idContractorGlobal === program.idContractorGlobal)
                        );
                    }
                    setData(filteredData)
                    setViewState('view')
                    setTotalCount(filteredData.length)
                    setLoadingStatus(LoadingStatus.Completed)
                    setIsSubmitting(false)
                    setSelectedItem(undefined)
                })
                break;
            case 'edit':
                renderEditor();
                break;
            case 'create':
                renderCreator();
                break;
            default:
                setModalWindow(<></>)
        }
    }, [viewState, contractors, isAdministrator])



    function renderEditor() {
        LPDataProvider?.getById(selectedItem?.idGlobal as string, (entity) => {
            setModalWindow(
                <LoyaltyProgramCreatorModal 
                    variant='edit' 
                    data={entity}
                    save={(model) => {
                        LPDataProvider?.update(selectedItem?.idGlobal as string, model, () => {
                            setViewState("refresh");
                            setModalWindow(<></>);
                        })
                    }}
                    cancel={() => {
                        setViewState("view");
                        setModalWindow(<></>);
                    }}
                    lockFromPermission={lockFromPermission}
                />
            )
        })
    }

    function renderCreator(): void {
        const defaultSettings = {
            Url: '',
            Name: '',
            Proxy: {
                Use: false,
                Port: 0,
                Address: '',
                User: '',
                Password: ''
            },
            PinCode: '',
            Timeout: 0,
            CardPrefix: '',
            StoreDepartmentId: '',
            StoreDepartmentKey: '',
            IsAutoSendAllCheques: false
        };
    
        const defaultParams = {
            SmsTemplate: '',
            IDActionGroup: 0,
            MinSumForDebit: 0,
            DebitPermitBySms: false,
            SmsDebitTemplate: '',
            SmsValidationTemplate: '',
            UsePersonalAdditionSale: false
        };

        let idContractorGlobalValue = '';
        if (!isAdministrator && contractors.length > 0) {
            idContractorGlobalValue = contractors[0].idContractorGlobal;
        } else {
            idContractorGlobalValue = '';
        }

        const newProgram: ILoyaltyProgramEditDTO = {
            idGlobal: uuidv4(),
            name: '',
            displayName: '',
            settings: JSON.stringify(defaultSettings),
            params: JSON.stringify(defaultParams),
            compatibility: false,
            type: 16, //SailPlay
            sortOrder: 0,
            isEnabled: false,
            incompatibleDiscount2CardType: [],
            incompatibleDiscount2Program: [],
            incompatibleLoyaltyProgram: [],
            idContractorGlobal: idContractorGlobalValue 
        };
        setModalWindow(
            <LoyaltyProgramCreatorModal
                data={newProgram}
                variant='create'
                save={(data: ILoyaltyProgramEditDTO) => {
                    LPDataProvider.create(data, (id: string) => {
                        setViewState("refresh");
                        setModalWindow(<></>);
                    })
                }}
                cancel={() => {
                    setViewState("view");
                    setModalWindow(<></>);
                }}
            />
        )
    }

    return (
        <PluginWrapper>
            {
                checkAccessStatus(props.plugin.permission as IPermission, userContext.userPermission) && <ToolbarWrapper>
                    <LoyaltyProgramPanel
                        add={{
                            onClick: () => {
                                setViewState('create')
                            }
                        }}
                        refresh={{
                            onClick: () => {
                                setViewState('refresh')
                            }
                        }}
                        edit={{
                            onClick: () => setViewState('edit'),
                            disabled: selectedItem === undefined || selectedItem?.isDeleted 
                        }}
                        permission={props.plugin.permission}
                    />
                    
                </ToolbarWrapper>
            }
            {
                (isSubmitting || isContractorsLoading) ? <Spinner />
                    :
                    <DefaultGrid
                        gridId={props.gridId}
                        data={data}
                        loadingStatus={loadingStatus}
                        setLoadingStatus={setLoadingStatus}
                        separator
                        singleDirectory
                        totalCount={totalCount}
                        filter={gridFilter}
                        dataProvider={LPDataProvider}
                        plugin={props.plugin}
                        selectedItem={selectedItem}
                        selectedItems={selectedItems}
                        multipleSelect={props.multipleSelect}
                        onDoubleClick={(row) => props.onDoubleClick?.(row)}
                        onSelect={(row) => {
                            setSelectedItem(row)
                            props.onSelect?.(row)
                        }}
                        onMultipleSelect={(rows) => {
                            setSelectedItems(rows)
                            props?.onMultipleSelect?.(rows)
                        }}
                        onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                        onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                        onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                        onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                        onEnter={() => setViewState("edit")}
                    />
            }
            {modalWindow}
        </PluginWrapper>
    )
}

